import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, GraduationCap, MapPin, Calendar, User,
  Mail, Phone, Pencil, Trash2, ChevronDown, ChevronUp,
  X, Sparkles, BookOpen, DollarSign, ClipboardList,
  ArrowRight, Users, TrendingUp, ExternalLink, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import CollegeSearchCombobox, { CollegeLogo } from '../components/CollegeSearchCombobox';

/* ─── Constants ─────────────────────────────────────────────── */

const VISIT_TYPES = [
  { value: 'CAMPUS_TOUR', label: 'Campus Tour' },
  { value: 'INFO_SESSION', label: 'Info Session' },
  { value: 'INTERVIEW', label: 'Interview' },
  { value: 'OPEN_DAY', label: 'Open Day' },
  { value: 'VIRTUAL', label: 'Virtual Visit' },
  { value: 'OTHER', label: 'Other' },
];

const APP_STATUSES = [
  { value: 'NOT_APPLYING',     label: 'Not Applying',      color: 'text-slate-500', bg: 'bg-slate-100' },
  { value: 'CONSIDERING',      label: 'Considering',        color: 'text-blue-600',  bg: 'bg-blue-50' },
  { value: 'PLANNING_TO_APPLY',label: 'Planning to Apply',  color: 'text-amber-600', bg: 'bg-amber-50' },
  { value: 'APPLIED',          label: 'Applied',            color: 'text-violet-600',bg: 'bg-violet-50' },
  { value: 'ACCEPTED',         label: 'Accepted',           color: 'text-green-600', bg: 'bg-green-50' },
  { value: 'REJECTED',         label: 'Rejected',           color: 'text-red-600',   bg: 'bg-red-50' },
  { value: 'WAITLISTED',       label: 'Waitlisted',         color: 'text-orange-500',bg: 'bg-orange-50' },
];

const statusCfg  = (s) => APP_STATUSES.find(x => x.value === s) || APP_STATUSES[1];
const visitLabel = (v) => VISIT_TYPES.find(x => x.value === v)?.label || v;
const emptyContact = () => ({ name: '', role: '', email: '', phone: '', notes: '' });

const emptyForm = () => ({
  collegeName: '', schoolUrl: '', scorecardData: null, collegeApplicationId: '',
  visitDate: new Date().toISOString().split('T')[0],
  visitType: 'CAMPUS_TOUR', location: '',
  contacts: [emptyContact()],
  overallRating: '',
  firstImpression: '', academicFit: '', socialFit: '', campusVibe: '',
  pros: [''], cons: [''], programsDiscussed: [''],
  financialAidNotes: '', admissionNotes: '',
  applicationStatus: 'CONSIDERING', followUpActions: [''], generalNotes: '',
});

const visitToForm = (v) => ({
  collegeName: v.collegeName || '',
  schoolUrl: v.schoolUrl || '',
  scorecardData: v.scorecardData || null,
  collegeApplicationId: v.collegeApplicationId || '',
  visitDate: v.visitDate ? new Date(v.visitDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
  visitType: v.visitType || 'CAMPUS_TOUR',
  location: v.location || '',
  contacts: Array.isArray(v.contacts) && v.contacts.length ? v.contacts : [emptyContact()],
  overallRating: v.overallRating || '',
  firstImpression: v.firstImpression || '',
  academicFit: v.academicFit || '',
  socialFit: v.socialFit || '',
  campusVibe: v.campusVibe || '',
  pros: v.pros?.length ? v.pros : [''],
  cons: v.cons?.length ? v.cons : [''],
  programsDiscussed: v.programsDiscussed?.length ? v.programsDiscussed : [''],
  financialAidNotes: v.financialAidNotes || '',
  admissionNotes: v.admissionNotes || '',
  applicationStatus: v.applicationStatus || 'CONSIDERING',
  followUpActions: v.followUpActions?.length ? v.followUpActions : [''],
  generalNotes: v.generalNotes || '',
});

const formToPayload = (f) => ({
  collegeName: f.collegeName.trim(),
  visitDate: f.visitDate,
  visitType: f.visitType,
  location: f.location.trim() || null,
  contacts: f.contacts.filter(c => c.name.trim()),
  schoolUrl: f.schoolUrl || null,
  scorecardData: f.scorecardData || null,
  collegeApplicationId: f.collegeApplicationId || null,
  overallRating: f.overallRating || null,
  firstImpression: f.firstImpression.trim() || null,
  academicFit: f.academicFit.trim() || null,
  socialFit: f.socialFit.trim() || null,
  campusVibe: f.campusVibe.trim() || null,
  pros: f.pros.filter(Boolean),
  cons: f.cons.filter(Boolean),
  programsDiscussed: f.programsDiscussed.filter(Boolean),
  financialAidNotes: f.financialAidNotes.trim() || null,
  admissionNotes: f.admissionNotes.trim() || null,
  applicationStatus: f.applicationStatus,
  followUpActions: f.followUpActions.filter(Boolean),
  generalNotes: f.generalNotes.trim() || null,
});

/* ─── Small helpers ─────────────────────────────────────────── */

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mobile;
};

const Field = ({ label, required, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium">
      {label}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const SectionDivider = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
      <Icon className="w-3.5 h-3.5" />
      <span className="text-[11px] font-bold uppercase tracking-[0.08em]">{title}</span>
    </div>
    <div className="flex-1 h-px bg-border" />
  </div>
);

const StarRating = ({ value, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? '' : n)}
        className={`w-9 h-9 flex items-center justify-center rounded-md text-xl transition-colors active:scale-95
          ${n <= (value || 0) ? 'text-amber-400' : 'text-slate-200 hover:text-amber-300'}`}
      >★</button>
    ))}
  </div>
);

const TagListEditor = ({ value, onChange, placeholder }) => {
  const items = value.length ? value : [''];
  const update = (idx, val) => {
    const next = [...items]; next[idx] = val;
    onChange(next.filter((v, i) => v || i < next.length - 1));
  };
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <Input value={item} onChange={e => update(idx, e.target.value)} placeholder={placeholder} className="h-9 text-sm" />
          {items.length > 1 && (
            <button type="button" onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, ''])}
        className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors py-1">
        <Plus className="w-3.5 h-3.5" /> Add item
      </button>
    </div>
  );
};

const ContactsEditor = ({ value, onChange }) => {
  const contacts = value.length ? value : [emptyContact()];
  const update = (idx, field, val) => onChange(contacts.map((c, i) => i === idx ? { ...c, [field]: val } : c));
  return (
    <div className="space-y-3">
      {contacts.map((c, idx) => (
        <div key={idx} className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Person {idx + 1}</span>
            {contacts.length > 1 && (
              <button type="button" onClick={() => onChange(contacts.filter((_, i) => i !== idx))}
                className="w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-destructive transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              ['name', 'Name', 'Jane Smith'],
              ['role', 'Role / Title', 'Admissions Officer'],
              ['email', 'Email', 'jane@university.edu'],
              ['phone', 'Phone', '+1 555 000 0000'],
            ].map(([key, label, ph]) => (
              <div key={key}>
                <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
                <Input value={c[key]} onChange={e => update(idx, key, e.target.value)}
                  placeholder={ph} className="h-8 text-sm" type={key === 'email' ? 'email' : 'text'} />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Notes</Label>
            <Input value={c.notes} onChange={e => update(idx, 'notes', e.target.value)}
              placeholder="What you discussed, follow-up needed…" className="h-8 text-sm" />
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...contacts, emptyContact()])}
        className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors py-1">
        <Plus className="w-3.5 h-3.5" /> Add contact
      </button>
    </div>
  );
};

const ScorecardPreview = ({ sc }) => {
  if (!sc) return null;
  const items = [
    sc.enrollmentSize != null && { label: 'Enrollment', value: Number(sc.enrollmentSize).toLocaleString() },
    sc.admissionRate != null && { label: 'Admit Rate', value: `${(sc.admissionRate * 100).toFixed(1)}%` },
    sc.tuitionOutOfState != null && { label: 'OOS Tuition', value: `$${Number(sc.tuitionOutOfState).toLocaleString()}` },
    sc.ownershipLabel && { label: 'Type', value: sc.ownershipLabel },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {items.map(({ label, value }) => (
        <span key={label} className="inline-flex items-center gap-1 text-xs bg-primary/5 border border-primary/20 text-primary rounded-md px-2 py-1">
          <span className="opacity-60">{label}</span>
          <span className="font-semibold">{value}</span>
        </span>
      ))}
    </div>
  );
};

/* ─── Visit Form ─────────────────────────────────────────────── */

const VisitForm = ({ form, setForm, onSubmit, saving, onCancel, title, collegeApplications }) => {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCollegeSelect = useCallback((school) => {
    if (!school) {
      setForm(f => ({ ...f, collegeName: '', schoolUrl: '', scorecardData: null }));
      return;
    }
    setForm(f => ({
      ...f,
      collegeName: school.name,
      schoolUrl: school.schoolUrl || '',
      scorecardData: school,
      location: f.location || [school.city, school.state].filter(Boolean).join(', '),
    }));
  }, [setForm]);

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 md:px-6 py-4 space-y-5">

        {/* ── Basic Info ── */}
        <SectionDivider icon={GraduationCap} title="Basic Information" />

        <Field label="College / University" required>
          <CollegeSearchCombobox value={form.collegeName} schoolUrl={form.schoolUrl} onSelect={handleCollegeSelect} />
          <ScorecardPreview sc={form.scorecardData} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Visit Date" required>
            <Input type="date" value={form.visitDate} onChange={e => set('visitDate', e.target.value)} className="h-9" />
          </Field>
          <Field label="Visit Type">
            <Select value={form.visitType} onValueChange={v => set('visitType', v)}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VISIT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Location" hint="Auto-filled from college selection — edit if needed">
          <Input value={form.location} onChange={e => set('location', e.target.value)}
            placeholder="Cambridge, MA  ·  Online  ·  etc." className="h-9" />
        </Field>

        <Field label="Link to a college on your list">
          <Select value={form.collegeApplicationId || 'none'} onValueChange={v => set('collegeApplicationId', v === 'none' ? '' : v)}>
            <SelectTrigger className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {collegeApplications.map(app => (
                <SelectItem key={app.id} value={app.id}>{app.collegeName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {/* ── People Met ── */}
        <SectionDivider icon={User} title="People Met" />
        <ContactsEditor value={form.contacts} onChange={v => set('contacts', v)} />

        {/* ── Impressions ── */}
        <SectionDivider icon={Sparkles} title="Impressions & Feelings" />

        <Field label="Overall Rating">
          <StarRating value={form.overallRating} onChange={v => set('overallRating', v)} />
        </Field>

        <Field label="First Impression">
          <Textarea value={form.firstImpression} onChange={e => set('firstImpression', e.target.value)}
            placeholder="What struck you immediately when you arrived?" rows={2} className="text-sm resize-none" />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Academic Fit">
            <Textarea value={form.academicFit} onChange={e => set('academicFit', e.target.value)}
              placeholder="How does the academic environment match your goals?" rows={2} className="text-sm resize-none" />
          </Field>
          <Field label="Social Fit">
            <Textarea value={form.socialFit} onChange={e => set('socialFit', e.target.value)}
              placeholder="Did you feel like you'd fit in?" rows={2} className="text-sm resize-none" />
          </Field>
        </div>

        <Field label="Campus Vibe">
          <Input value={form.campusVibe} onChange={e => set('campusVibe', e.target.value)}
            placeholder="Buzzing, serene, collegiate, urban…" className="h-9" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Pros">
            <TagListEditor value={form.pros} onChange={v => set('pros', v)} placeholder="A strength…" />
          </Field>
          <Field label="Cons">
            <TagListEditor value={form.cons} onChange={v => set('cons', v)} placeholder="A concern…" />
          </Field>
        </div>

        {/* ── Academic Details ── */}
        <SectionDivider icon={BookOpen} title="Academic Details" />

        <Field label="Programs / Departments Discussed">
          <TagListEditor value={form.programsDiscussed} onChange={v => set('programsDiscussed', v)} placeholder="Computer Science, Pre-Med…" />
        </Field>

        <Field label="Admission Notes">
          <Textarea value={form.admissionNotes} onChange={e => set('admissionNotes', e.target.value)}
            placeholder="SAT ranges, acceptance rates, what they look for…" rows={2} className="text-sm resize-none" />
        </Field>

        {/* ── Financial Aid ── */}
        <SectionDivider icon={DollarSign} title="Financial Aid" />
        <Textarea value={form.financialAidNotes} onChange={e => set('financialAidNotes', e.target.value)}
          placeholder="Merit scholarships, need-blind admissions, financial aid packages…" rows={2} className="text-sm resize-none" />

        {/* ── Planning ── */}
        <SectionDivider icon={ClipboardList} title="Planning" />

        <Field label="Application Status">
          <Select value={form.applicationStatus} onValueChange={v => set('applicationStatus', v)}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {APP_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Follow-Up Actions">
          <TagListEditor value={form.followUpActions} onChange={v => set('followUpActions', v)} placeholder="Email admissions office, request brochure…" />
        </Field>

        <Field label="General Notes">
          <Textarea value={form.generalNotes} onChange={e => set('generalNotes', e.target.value)}
            placeholder="Anything else worth remembering…" rows={3} className="text-sm resize-none" />
        </Field>

        {/* Bottom spacer so content clears the sticky footer */}
        <div className="h-2" />
      </div>

      {/* Sticky footer */}
      <div className="shrink-0 flex items-center justify-between gap-3 px-4 md:px-6 py-3 border-t border-border bg-background">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving} className="text-muted-foreground">
          Cancel
        </Button>
        <Button type="button" onClick={onSubmit} disabled={saving} size="sm" className="min-w-[110px]">
          {saving ? 'Saving…' : title}
        </Button>
      </div>
    </div>
  );
};

/* ─── Visit card ─────────────────────────────────────────────── */

const ScorecardGrid = ({ sc }) => {
  if (!sc) return null;
  const items = [
    sc.enrollmentSize != null   && { label: 'Enrollment',        value: `${Number(sc.enrollmentSize).toLocaleString()} students` },
    sc.admissionRate != null    && { label: 'Admit Rate',         value: `${(sc.admissionRate * 100).toFixed(1)}%` },
    sc.tuitionInState != null   && { label: 'In-State Tuition',   value: `$${Number(sc.tuitionInState).toLocaleString()}/yr` },
    sc.tuitionOutOfState != null&& { label: 'OOS Tuition',        value: `$${Number(sc.tuitionOutOfState).toLocaleString()}/yr` },
    sc.ownershipLabel           && { label: 'Institution Type',   value: sc.ownershipLabel },
    sc.medianEarnings10yr != null && { label: 'Median Earnings (10yr)', value: `$${Number(sc.medianEarnings10yr).toLocaleString()}/yr` },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div>
      <p className="label-swiss mb-2 flex items-center gap-1.5 text-muted-foreground">
        <TrendingUp className="w-3 h-3" /> Scorecard Data
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {items.map(({ label, value }) => (
          <div key={label} className="bg-white border border-border rounded-lg px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="text-sm font-semibold text-foreground mt-0.5 leading-tight">{value}</p>
          </div>
        ))}
      </div>
      {sc.domain && (
        <a href={`https://${sc.domain}`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2">
          <ExternalLink className="w-3 h-3" /> {sc.domain}
        </a>
      )}
    </div>
  );
};

const ratingStars = (n) => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : null;

const VisitCard = ({ visit, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const contacts = Array.isArray(visit.contacts) ? visit.contacts.filter(c => c.name) : [];
  const sc = visit.scorecardData;
  const status = statusCfg(visit.applicationStatus);

  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      {/* Header row */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <CollegeLogo schoolUrl={visit.schoolUrl} name={visit.collegeName} size={40} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[15px] text-foreground leading-snug">{visit.collegeName}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0">
                    {visitLabel(visit.visitType)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2.5 text-[12px] text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {format(new Date(visit.visitDate), 'MMM d, yyyy')}
                  </span>
                  {visit.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{visit.location}
                    </span>
                  )}
                  {visit.overallRating && (
                    <span className="text-amber-400 tracking-tight leading-none">{ratingStars(visit.overallRating)}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-1">
                <span className={`hidden sm:inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${status.color} ${status.bg}`}>
                  {status.label}
                </span>
                {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </div>

            {/* Collapsed preview — scorecard chips */}
            {!open && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className={`sm:hidden text-[11px] font-semibold px-2 py-0.5 rounded-full ${status.color} ${status.bg}`}>
                  {status.label}
                </span>
                {sc?.admissionRate != null && (
                  <span className="text-[11px] bg-muted/60 border border-border rounded px-2 py-0.5">
                    <span className="text-muted-foreground">Admit: </span><strong>{(sc.admissionRate * 100).toFixed(0)}%</strong>
                  </span>
                )}
                {sc?.enrollmentSize != null && (
                  <span className="text-[11px] bg-muted/60 border border-border rounded px-2 py-0.5">
                    <strong>{Number(sc.enrollmentSize).toLocaleString()}</strong><span className="text-muted-foreground"> students</span>
                  </span>
                )}
                {sc?.tuitionOutOfState != null && (
                  <span className="text-[11px] bg-muted/60 border border-border rounded px-2 py-0.5">
                    <strong>${Number(sc.tuitionOutOfState).toLocaleString()}</strong><span className="text-muted-foreground"> OOS</span>
                  </span>
                )}
                {!sc && visit.pros?.filter(Boolean).slice(0, 2).map((p, i) => (
                  <span key={i} className="text-[11px] bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">{p}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </button>

      {/* Action buttons — always visible */}
      <div className="flex items-center justify-end gap-1 px-4 sm:px-5 pb-3 -mt-2">
        <button onClick={() => onEdit(visit)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-md hover:bg-muted/60 transition-colors">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        <button onClick={() => onDelete(visit)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive px-2 py-1.5 rounded-md hover:bg-destructive/10 transition-colors">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>

      {/* Expanded detail */}
      {open && (
        <div className="border-t border-border px-4 sm:px-5 py-4 space-y-4 bg-muted/20">

          <ScorecardGrid sc={sc} />

          {visit.firstImpression && (
            <div>
              <p className="label-swiss mb-1">First Impression</p>
              <p className="text-sm text-foreground leading-relaxed">{visit.firstImpression}</p>
            </div>
          )}

          {(visit.academicFit || visit.socialFit || visit.campusVibe) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {visit.academicFit && <div><p className="label-swiss mb-1">Academic Fit</p><p className="text-sm leading-relaxed">{visit.academicFit}</p></div>}
              {visit.socialFit && <div><p className="label-swiss mb-1">Social Fit</p><p className="text-sm leading-relaxed">{visit.socialFit}</p></div>}
              {visit.campusVibe && <div><p className="label-swiss mb-1">Campus Vibe</p><p className="text-sm leading-relaxed">{visit.campusVibe}</p></div>}
            </div>
          )}

          {(visit.pros?.filter(Boolean).length > 0 || visit.cons?.filter(Boolean).length > 0) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="label-swiss mb-1.5 text-green-700">Pros</p>
                <ul className="space-y-1">{visit.pros.filter(Boolean).map((p, i) => (
                  <li key={i} className="text-sm flex items-start gap-1.5"><span className="text-green-600">+</span>{p}</li>
                ))}</ul>
              </div>
              <div>
                <p className="label-swiss mb-1.5 text-red-600">Cons</p>
                <ul className="space-y-1">{visit.cons.filter(Boolean).map((c, i) => (
                  <li key={i} className="text-sm flex items-start gap-1.5"><span className="text-red-500">−</span>{c}</li>
                ))}</ul>
              </div>
            </div>
          )}

          {visit.programsDiscussed?.filter(Boolean).length > 0 && (
            <div>
              <p className="label-swiss mb-1.5">Programs Discussed</p>
              <div className="flex flex-wrap gap-1.5">
                {visit.programsDiscussed.filter(Boolean).map((p, i) => (
                  <span key={i} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-0.5">{p}</span>
                ))}
              </div>
            </div>
          )}

          {(visit.admissionNotes || visit.financialAidNotes) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {visit.admissionNotes && <div><p className="label-swiss mb-1">Admission Notes</p><p className="text-sm leading-relaxed">{visit.admissionNotes}</p></div>}
              {visit.financialAidNotes && <div><p className="label-swiss mb-1">Financial Aid</p><p className="text-sm leading-relaxed">{visit.financialAidNotes}</p></div>}
            </div>
          )}

          {contacts.length > 0 && (
            <div>
              <p className="label-swiss mb-2">People Met</p>
              <div className="space-y-2">
                {contacts.map((c, i) => (
                  <div key={i} className="flex items-start gap-3 p-2.5 bg-white border border-border rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-primary">{c.name?.[0]?.toUpperCase() || '?'}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm">{c.name}</span>
                        {c.role && <span className="text-xs text-muted-foreground">{c.role}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-0.5">
                        {c.email && (
                          <a href={`mailto:${c.email}`} className="text-xs text-primary hover:underline flex items-center gap-1">
                            <Mail className="w-3 h-3" />{c.email}
                          </a>
                        )}
                        {c.phone && (
                          <a href={`tel:${c.phone}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" />{c.phone}
                          </a>
                        )}
                      </div>
                      {c.notes && <p className="text-xs text-muted-foreground mt-1 italic">{c.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {visit.followUpActions?.filter(Boolean).length > 0 && (
            <div>
              <p className="label-swiss mb-1.5">Follow-Up Actions</p>
              <ul className="space-y-1">
                {visit.followUpActions.filter(Boolean).map((a, i) => (
                  <li key={i} className="text-sm flex items-start gap-1.5">
                    <ArrowRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />{a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {visit.generalNotes && (
            <div>
              <p className="label-swiss mb-1">General Notes</p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{visit.generalNotes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Page ───────────────────────────────────────────────────── */

const CollegeVisitsPage = () => {
  const { api } = useAuth();
  const isMobile = useIsMobile();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [collegeApplications, setCollegeApplications] = useState([]);
  const [collegeAppsLoaded, setCollegeAppsLoaded] = useState(false);

  useEffect(() => {
    fetchVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sheetOpen && !collegeAppsLoaded) {
      fetchCollegeApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen]);

  const fetchVisits = async () => {
    try {
      const res = await api.get('/college-visits');
      setVisits(res.data.visits || []);
    } catch { toast.error('Failed to load college visits'); }
    finally { setLoading(false); }
  };

  const fetchCollegeApplications = async () => {
    try {
      const res = await api.get('/college-applications');
      setCollegeApplications(res.data.applications || []);
    } catch {
      // optional field — silently ignore
    } finally {
      setCollegeAppsLoaded(true);
    }
  };

  const openCreate = () => { setEditingVisit(null); setForm(emptyForm()); setSheetOpen(true); };
  const openEdit   = (v)  => { setEditingVisit(v);   setForm(visitToForm(v)); setSheetOpen(true); };

  const handleSave = async () => {
    if (!form.collegeName.trim()) { toast.error('College name is required'); return; }
    if (!form.visitDate)          { toast.error('Visit date is required'); return; }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingVisit) {
        const res = await api.put(`/college-visits/${editingVisit.id}`, payload);
        setVisits(v => v.map(x => x.id === editingVisit.id ? res.data.visit : x));
        toast.success('Visit updated');
      } else {
        const res = await api.post('/college-visits', payload);
        setVisits(v => [res.data.visit, ...v]);
        toast.success('Visit logged — confirmation email sent!');
      }
      setSheetOpen(false);
    } catch { toast.error('Failed to save visit'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/college-visits/${deleteTarget.id}`);
      setVisits(v => v.filter(x => x.id !== deleteTarget.id));
      toast.success('Visit deleted');
    } catch { toast.error('Failed to delete visit'); }
    finally { setDeleteTarget(null); }
  };

  /* Shared form panel rendered inside either Sheet or Dialog */
  const formPanel = (
    <VisitForm
      form={form}
      setForm={setForm}
      onSubmit={handleSave}
      saving={saving}
      onCancel={() => setSheetOpen(false)}
      title={editingVisit ? 'Save Changes' : 'Log Visit'}
      collegeApplications={collegeApplications}
    />
  );

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight">College Visits</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Log visits, record impressions, and track contacts.
          </p>
        </div>
        <Button onClick={openCreate} size="sm" className="gap-1.5 shrink-0">
          <Plus className="w-3.5 h-3.5" /> Log Visit
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-muted-foreground text-sm animate-pulse">Loading visits…</div>
      ) : visits.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-xl px-6">
          <GraduationCap className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No college visits yet</p>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Start tracking your visits, people you meet, and your thoughts.</p>
          <Button onClick={openCreate} size="sm" variant="outline" className="gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Log your first visit
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {visits.map(visit => (
            <VisitCard key={visit.id} visit={visit} onEdit={openEdit} onDelete={setDeleteTarget} />
          ))}
        </div>
      )}

      {/* Form — Sheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="bottom" className="p-0 h-[95dvh] rounded-t-2xl flex flex-col">
            <SheetHeader className="px-4 pt-4 pb-2 border-b border-border shrink-0">
              <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-3" />
              <SheetTitle className="text-left text-base">
                {editingVisit ? 'Edit Visit' : 'Log College Visit'}
              </SheetTitle>
            </SheetHeader>
            <div className="flex-1 min-h-0">
              {formPanel}
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
          <DialogContent className="max-w-2xl p-0 h-[90vh] flex flex-col gap-0">
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-border shrink-0">
              <DialogTitle>{editingVisit ? 'Edit Visit' : 'Log College Visit'}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0">
              {formPanel}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Visit?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your visit log for <strong>{deleteTarget?.collegeName}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CollegeVisitsPage;
